import React from "react";
import { createColumnHelper } from "@tanstack/react-table";
import dayjs from "dayjs";
import { Box, Stack, Typography } from "@mui/material";

const columnHelper = createColumnHelper();

export const defaultTabs = [
  { name: "All", value: "all", isActive: true, tabCount: 0 },
  { name: "Import", value: "import", isActive: false, tabCount: 0 },
  { name: "Subscribe", value: "subscribe", isActive: false, tabCount: 0 },
  {
    name: "Monthly Charge",
    value: "monthlyCharge",
    isActive: false,
    tabCount: 0,
  },
  { name: "Added Device", value: "addDevice", isActive: false, tabCount: 0 },
  {
    name: "Remove Device",
    value: "removeDevice",
    isActive: false,
    tabCount: 0,
  },
  { name: "Unsubscribe", value: "unsubscribe", isActive: false, tabCount: 0 },
];

const getTypeLabel = (type) => {
  if (!type) return "";
  const mapping = {
    import: "Import",
    subscribe: "Subscribe",
    monthlycharge: "Monthly Charge",
    adddevice: "Added Device",
    removedevice: "Remove Device",
    unsubscribe: "Unsubscribe",
  };
  return mapping[type.toLowerCase()] || type;
};

const getDescription = (tx) => {
  const type = tx.type ? tx.type.toLowerCase() : "";
  const p = tx.descriptionParams || {};

  const getParam = (key) => {
    const lKey = key.toLowerCase();
    const actualKey = Object.keys(p).find((k) => k.toLowerCase() === lKey);
    return actualKey ? p[actualKey] : undefined;
  };

  const isPlural = ["monthlycharge", "adddevice"].includes(type);
  const deviceLabel = isPlural ? "Devices" : "Device";

  const renderRow = (label, value) => (
    <Box key={label}>
      <Typography
        variant='body2'
        component='span'
        sx={{ color: "text.secondary", fontSize: "13px" }}
      >
        {label}:{" "}
      </Typography>
      <Typography
        variant='body2'
        component='span'
        sx={{ fontWeight: "bold", fontSize: "13px" }}
      >
        {value}
      </Typography>
    </Box>
  );

  switch (type) {
    case "import":
      return (
        <Stack spacing={0}>
          {renderRow("Account", getParam("account") || "-")}
          {renderRow("License Key", getParam("key") || "-")}
        </Stack>
      );
    case "subscribe":
    case "monthlycharge":
    case "adddevice":
    case "removedevice":
    case "unsubscribe":
      return (
        <Stack spacing={0}>
          {renderRow("Site", getParam("site") || "Org Level")}
          {renderRow(
            deviceLabel,
            getParam("devices") || getParam("device") || 0,
          )}
          {renderRow("Days", getParam("days") || 0)}
          {renderRow("Service", getParam("service") || "-")}
        </Stack>
      );
    default:
      return "-";
  }
};

export const getTransactionsTableColumns = ({ theme }) => [
  columnHelper.accessor(
    (row) => dayjs.unix(row.timestamp).format("YYYY-MM-DD"),
    {
      id: "formattedDate",
      header: "Date",
      sortingFn: (rowA, rowB) => {
        return rowA.original.timestamp - rowB.original.timestamp;
      },
      cell: (info) => (
        <Typography sx={{ fontSize: "13px", color: "#111" }}>
          {info.getValue()}
        </Typography>
      ),
    },
  ),
  columnHelper.accessor("type", {
    header: "Type",
    cell: (info) => (
      <Typography sx={{ fontSize: "13px", color: "#111" }}>
        {getTypeLabel(info.getValue())}
      </Typography>
    ),
  }),
  columnHelper.accessor("change", {
    header: "Amount",
    sortingFn: (rowA, rowB, columnId) => {
      const a = Number(rowA.getValue(columnId)) || 0;
      const b = Number(rowB.getValue(columnId)) || 0;
      return a - b;
    },
    cell: (info) => {
      const value = info.getValue();
      const isPositive = value > 0;
      return (
        <Typography
          sx={{
            fontWeight: "bold",
            fontSize: "13px",
            color: isPositive ? "#2E7D32" : "#000000",
          }}
        >
          {isPositive ? `+${value.toLocaleString()}` : value.toLocaleString()}
        </Typography>
      );
    },
  }),
  columnHelper.accessor(
    (row) => {
      const type = row.type ? row.type.toLowerCase() : "";
      const p = row.descriptionParams || {};
      const getParam = (key) => {
        const lKey = key.toLowerCase();
        const actualKey = Object.keys(p).find((k) => k.toLowerCase() === lKey);
        return actualKey ? p[actualKey] : undefined;
      };
      if (type === "import") {
        return `Account: ${getParam("account") || ""} License Key: ${getParam("key") || ""}`;
      }
      return `Site: ${getParam("site") || ""} ${getParam("devices") || getParam("device") || ""} Days: ${getParam("days") || ""} Service: ${getParam("service") || ""}`;
    },
    {
      id: "description",
      header: "Description",
      cell: (info) => getDescription(info.row.original),
    },
  ),
  columnHelper.accessor("balance", {
    header: "Balance After",
    sortingFn: (rowA, rowB, columnId) => {
      const a = Number(rowA.getValue(columnId)) || 0;
      const b = Number(rowB.getValue(columnId)) || 0;
      return a - b;
    },
    cell: (info) => (
      <Typography sx={{ fontSize: "13px", color: "#111" }}>
        {info.getValue()?.toLocaleString()}
      </Typography>
    ),
  }),
];
